export const PROCESS_TYPES = [
    { code: '10', group: 'Processes' },
    { code: '20', group: 'Processes' },
    { code: '30', group: 'Processes' },
    { code: '40', group: 'Processes', creatable: false },
    { code: '50', group: 'Processes'},
    { code: '100', group: 'Test' },
    { code: '101', group: 'Test' },
    { code: '102', group: 'Test' },
    { code: '103', group: 'Test' },
];

export function processTypeLabel(code, t) {
    return t(`processType.${code}`, { defaultValue: `Type ${code}` });
}

export const MONTHS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

export function monthLabel(value, t) {
    return t(`months.${value}`, { defaultValue: value });
}

const SIRE_PERIOD_FIELDS = [
    {
        key: 'year',
        labelKey: 'processFields.year.label',
        hintKey: 'processFields.year.hint',
        type: 'number',
        required: true,
        defaultValue: () => String(new Date().getFullYear()),
    },
    {
        key: 'month',
        labelKey: 'processFields.month.label',
        type: 'select',
        required: true,
        options: MONTHS,
        optionsNamespace: 'months',
    },
];

// Extra fields each process type needs in its configMetadata, rendered as a form instead of raw JSON.
export const PROCESS_TYPE_FIELDS = {
    10: SIRE_PERIOD_FIELDS,
    20: SIRE_PERIOD_FIELDS,
    30: [
        {
            key: 'folderName',
            labelKey: 'processFields.folderName.label',
            hintKey: 'processFields.folderName.hint',
            required: true,
        },
    ],
};

export function processTypeFields(code) {
    return PROCESS_TYPE_FIELDS[code] ?? [];
}

export function defaultFieldValues(code) {
    const values = {};
    for (const field of processTypeFields(code)) {
        if (field.defaultValue !== undefined) {
            values[field.key] = typeof field.defaultValue === 'function' ? field.defaultValue() : field.defaultValue;
        }
    }
    return values;
}

// Builds the configMetadata payload sent when creating a process, from the form's field values.
export function buildProcessConfigMetadata(code, fieldValues) {
    if (code === '10' || code === '20') {
        const { year, month } = fieldValues;
        if (!year || !month) return {};
        return { period: `${year}${String(month).padStart(2, '0')}` };
    }
    const fields = processTypeFields(code);
    const result = {};
    for (const field of fields) {
        const value = fieldValues[field.key];
        if (value !== undefined && value !== '') {
            result[field.key] = value;
        }
    }
    return result;
}

export const PROCESS_STATUS_TONES = {
    1: 'neutral',
    2: 'info',
    3: 'success',
    4: 'danger',
    5: 'warning',
};

export function statusInfo(code, t) {
    return {
        label: t(`processStatus.${code}`, { defaultValue: `Status ${code}` }),
        tone: PROCESS_STATUS_TONES[code] ?? 'neutral',
    };
}

export function isActiveStatus(code) {
    return code === '2' || code === '5';
}

export function stepTypeLabel(code, t) {
    return t(`stepType.${code}`, { defaultValue: `Step ${code}` });
}

export const MESSAGE_TYPE_TONES = {
    1: 'info',
    2: 'warning',
};

export function messageTypeInfo(code, t) {
    return {
        label: t(`messageType.${code}`, { defaultValue: `Type ${code}` }),
        tone: MESSAGE_TYPE_TONES[code] ?? 'neutral',
    };
}

export function serviceTypeLabel(code, t) {
    return t(`paymentServiceType.${code}`, { defaultValue: code });
}

// SUNAT tax-document type names are official Peruvian legal terms and are always
// shown in Spanish, regardless of the interface language.
export const PAYMENT_TYPES = {
    0: 'Otros',
    '01': 'Factura',
    '02': 'Recibo por Honorarios',
    '03': 'Boleta de Venta',
    '05': 'Tiquete de Transporte Aereo',
    '07': 'Nota de Credito',
    '08': 'Nota de Debito',
    10: 'Recibo por Arrendamiento',
    12: 'Ticket',
    14: 'Recibo por Servicios Publicos',
    100: 'No Reconocido',
    110: 'TMP',
};

export function paymentTypeLabel(code) {
    return PAYMENT_TYPES[code] ?? code;
}

// SUNAT and Google Drive are proper nouns and are not translated.
export const DOCUMENT_ORIGINS = {
    0: 'SUNAT',
    1: 'Google Drive',
};

export function originLabel(code) {
    return DOCUMENT_ORIGINS[code] ?? code;
}
